import React, { useEffect, useState, useRef } from 'react';
import { SelectBox } from 'devextreme-react/select-box';
import DataGrid, { Column, Editing, Toolbar, Item } from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import axios from 'axios';
import EditSalaryPopup from './EditSalaryPopup';
import './App.css';

const API_BASE = 'https://qbo.omnivistaweb.com';

interface SalaryDetail {
  pay_detail_id: number;
  item_id: string;
  amount: number;
  notes?: string;
  temp_id: string;
}

interface SalaryRow {
  employee_id: string;
  start_date: string;
  pay_detail_id: number;
  pay_type: string;
  details: SalaryDetail[];
}

const generateTempId = () =>
  'temp_' + Math.random().toString(36).substring(2, 9);

const EmployeeCenter = () => {
  const [employees, setEmployees] = useState<string[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const [salaryRows, setSalaryRows] = useState<SalaryRow[]>([]);
  const [expenseItems, setExpenseItems] = useState<{ name: string; value: string }[]>([]);
  const [payTypes, setPayTypes] = useState<{ name: string; value: string }[]>([]);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [editData, setEditData] = useState<SalaryRow | null>(null);
  const [randomMode, setRandomMode] = useState<boolean>(false);

  const gridRef = useRef<any>(null);
  const detailGridRef = useRef<any>(null);

  useEffect(() => {
    axios.get(`${API_BASE}/api/accounts/expenses?includeParents=true`).then(res => {
      setExpenseItems(res.data.map((item: any) => ({
        name: item.parent_name ? `${item.parent_name}:${item.name}` : item.name,
        value: item.value
      })));
    });

    axios.get(`${API_BASE}/api/employees/pay-types`).then(res => {
      setPayTypes(res.data.map((payType: any) => ({
        name: payType.type,
        value: payType.type
      })));
    });

    axios.get(`${API_BASE}/api/employees`).then(res => {
      setEmployees(res.data.sort((a: string, b: string) => a.localeCompare(b)));
    });

    axios.get(`${API_BASE}/api/settings/qbo`).then(res => {
      setRandomMode(res.data.random_salary_data === true);
    });
  }, []);

  const handleSelect = async (name: string) => {
    setSelectedEmployee(name);
    const res = await axios.get(`${API_BASE}/api/employees/salary`, { params: { name } });
    const withTempIds = res.data.map((row: SalaryRow) => ({
      ...row,
      details: row.details.map((d: any, idx: number) => ({
        ...d,
        temp_id: `${row.pay_detail_id}_${idx + 1}`
      }))
    }));
    setSalaryRows(withTempIds);
    setSelectedRow(withTempIds.length > 0 ? withTempIds[0].pay_detail_id : null);
  };

  const handleAddRow = async () => {
    if (!selectedEmployee || randomMode) return;

    const res = await axios.get(`${API_BASE}/api/employees/pay-detail-id/next`);
    const newRow: SalaryRow = {
      employee_id: selectedEmployee,
      pay_detail_id: res.data.next,
      start_date: new Date().toISOString().split('T')[0],
      pay_type: '',
      details: []
    };
    setEditData(newRow);
  };

  const handleSave = () => {
    if (randomMode) return;

    const sanitized = salaryRows.map(row => ({
      ...row,
      details: row.details.map(({ temp_id, ...rest }) => rest)
    }));

    axios.post(`${API_BASE}/api/employees/salary/save`, { name: selectedEmployee, rows: sanitized })
      .then(() => alert('✅ Salary data saved!'))
      .catch(() => alert('❌ Failed to save'));
  };

  const handleDeleteRow = (pay_detail_id: number) => {
    if (randomMode) return;
    setSalaryRows(prev => prev.filter(r => r.pay_detail_id !== pay_detail_id));
  };

  const selectedDetails = salaryRows.find(r => r.pay_detail_id === selectedRow)?.details || [];
  const totalAmount = selectedDetails.reduce((sum, d) => sum + (d.amount || 0), 0);

  return (
    <div className="dashboard-wrapper">
      <div className="box">
        <SelectBox
          items={employees}
          value={selectedEmployee}
          onValueChanged={(e) => handleSelect(e.value)}
          placeholder="Select Employee"
          searchEnabled
          width={300}
          stylingMode="filled"
        />
      </div>

      <div className="box">
        <DataGrid
          ref={gridRef}
          dataSource={salaryRows}
          keyExpr="pay_detail_id"
          showBorders
          focusedRowEnabled
          focusedRowKey={selectedRow}
          onFocusedRowChanged={(e) => setSelectedRow(e.row?.data?.pay_detail_id || null)}
          disabled={randomMode}
        >
          <Editing allowUpdating allowDeleting={!randomMode} mode="row" useIcons />
          <Toolbar>
            <Item location="before">
              <Button icon="add" text="Add" type="default" stylingMode="contained" onClick={handleAddRow} />
            </Item>
            <Item location="before">
              <Button icon="save" text="Save" type="success" stylingMode="contained" onClick={handleSave} />
            </Item>
          </Toolbar>
          <Column dataField="employee_id" caption="Employee ID" allowEditing={false} />
          <Column dataField="start_date" caption="Start Date" dataType="date" />
          <Column
            dataField="pay_type"
            caption="Type"
            lookup={{
              dataSource: payTypes,
              valueExpr: 'value',
              displayExpr: 'name'
            }}
          />
          <Column
            caption="Total Amount"
            dataType="number"
            alignment="right"
            format={{ type: 'fixedPoint', precision: 2 }}
            calculateCellValue={(rowData: SalaryRow) =>
              (rowData.details || []).reduce((sum, d) => sum + (d.amount || 0), 0)
            }
          />
          <Column
            type="buttons"
            width={140}
            buttons={[
              {
                icon: 'edit',
                onClick: (e: any) => { if (!randomMode) setEditData(e.row.data); }
              },
              {
                icon: 'trash',
                onClick: (e: any) => handleDeleteRow(e.row.data.pay_detail_id)
              }
            ]}
          />
        </DataGrid>
      </div>

      {editData && (
        <EditSalaryPopup
          data={editData}
          onClose={(updated) => {
            if (updated) {
              const idx = salaryRows.findIndex(r => r.pay_detail_id === updated.pay_detail_id);
              const rows = [...salaryRows];
              if (idx > -1) rows[idx] = updated;
              else rows.push(updated);
              setSalaryRows(rows);
            }
            setEditData(null);
          }}
          expenseItems={expenseItems}
          payTypes={payTypes}
          randomMode={randomMode}
        />
      )}
    </div>
  );
};

export default EmployeeCenter;
